import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/shared/lib/utils'
import { SupplierInstallationEmissionReportDeleteConfirmDialog } from '@/features/suppliers/components/supplier-installation-emission-report-delete-confirm-dialog'
import { SupplierInstallationEmissionReportFormDialog } from '@/features/suppliers/components/supplier-installation-emission-report-form-dialog'
import {
  useSupplierInstallationEmissionReportMutations,
  useSupplierInstallationEmissionReportsQuery,
  type SupplierInstallationEmissionReportDto,
} from '@/features/suppliers/hooks/use-supplier-installation-emission-reports'
import { formatSupplierInstallationEmissionReportProductionRouteLabel } from '@/features/suppliers/lib/supplier-installation-emission-report-production-route-label'
import type {
  SupplierInstallationEmissionReportProductionRoute,
  SupplierInstallationEmissionReportYear,
} from '@artem-cbam-operator/common-server/supplier-installation-emission-report'
import { useOrganizationDocuments } from '@/shared/hooks/use-organization-documents'
import { useSession } from '@/shared/hooks/useSession'
import { handleServerError } from '@/shared/lib/handle-server-error'

type SupplierInstallationEmissionReportsSectionCardProps = {
  supplierOrganizationId: string
  supplierInstallationId: string
  canEdit: boolean
  variant?: 'card' | 'plain'
}

export function SupplierInstallationEmissionReportsSectionCard({
  supplierOrganizationId,
  supplierInstallationId,
  canEdit,
  variant = 'card',
}: SupplierInstallationEmissionReportsSectionCardProps) {
  const { t } = useTranslation(['installations', 'common'])
  const {
    session: {
      organization: { id: organizationId },
    },
  } = useSession()
  const { downloadDocument } = useOrganizationDocuments({ organizationId })

  const listQuery = useSupplierInstallationEmissionReportsQuery(
    supplierOrganizationId,
    supplierInstallationId
  )
  const mutations = useSupplierInstallationEmissionReportMutations(
    supplierOrganizationId,
    supplierInstallationId
  )

  const [createOpen, setCreateOpen] = useState(false)
  const [editReport, setEditReport] = useState<SupplierInstallationEmissionReportDto | null>(
    null
  )
  const [pendingDelete, setPendingDelete] = useState<{
    id: string
    label: string
  } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const rows = listQuery.data

  const handleDownload = useCallback(
    async (proofFilePath: string) => {
      const result = await downloadDocument(proofFilePath)
      if (!result.success) {
        toast.error(t('common:errors.downloadFile.generic'))
        return
      }
      toast.success(t('common:notifications.downloadSuccess'))
    },
    [downloadDocument, t]
  )

  const handleCreate = useCallback(
    async (payload: {
      year: SupplierInstallationEmissionReportYear
      cnCode: string
      productionRoute: SupplierInstallationEmissionReportProductionRoute
      specificEmbeddedEmissions: number
      proofFilePath: string
    }) => {
      try {
        await mutations.createEmissionReport.mutateAsync(payload)
        setCreateOpen(false)
        toast.success(
          t(
            'installations:suppliers.edit.installations.edit.emissionReports.notifications.createSuccess'
          )
        )
      } catch (error) {
        handleServerError(error)
      }
    },
    [mutations.createEmissionReport, t]
  )

  const handleEdit = useCallback(
    async (payload: {
      cnCode: string
      productionRoute?: SupplierInstallationEmissionReportProductionRoute
      proofFilePath?: string | null
    }) => {
      if (!editReport) {
        return
      }
      try {
        await mutations.updateEmissionReport.mutateAsync({
          emissionReportId: editReport.id,
          payload,
        })
        setEditReport(null)
        toast.success(
          t(
            'installations:suppliers.edit.installations.edit.emissionReports.notifications.updateSuccess'
          )
        )
      } catch (error) {
        handleServerError(error)
      }
    },
    [editReport, mutations.updateEmissionReport, t]
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) {
      return
    }
    try {
      await mutations.deleteEmissionReport.mutateAsync(pendingDelete.id)
      setPendingDelete(null)
      setDeleteDialogOpen(false)
      toast.success(
        t(
          'installations:suppliers.edit.installations.edit.emissionReports.notifications.deleteSuccess'
        )
      )
    } catch (error) {
      handleServerError(error)
    }
  }, [mutations.deleteEmissionReport, pendingDelete, t])

  const isMutating =
    mutations.createEmissionReport.isPending ||
    mutations.updateEmissionReport.isPending ||
    mutations.deleteEmissionReport.isPending

  const createButton =
    canEdit && rows !== undefined && rows.length > 0 ? (
      <Button type="button" onClick={() => setCreateOpen(true)}>
        <Plus className="size-4" />
        {t('common:actions.create')}
      </Button>
    ) : null

  const listContent = (
    <>
          {listQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows === undefined ? (
            <p className="text-muted-foreground">
              {t(
                'installations:suppliers.edit.installations.edit.emissionReports.loadError'
              )}
            </p>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-12">
              <Inbox className="size-10 text-muted-foreground/50" />
              <div className="space-y-1 text-center">
                <h3 className="text-lg font-semibold">
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.emptyTitle'
                  )}
                </h3>
                <p className="max-w-md text-sm text-muted-foreground">
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.emptyDescription'
                  )}
                </p>
              </div>
              {canEdit && (
                <Button type="button" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" />
                  {t('common:actions.create')}
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="group/row">
                    <TableHead className="bg-background group-hover/row:bg-muted">
                      {t(
                        'installations:suppliers.edit.installations.edit.emissionReports.fields.year'
                      )}
                    </TableHead>
                    <TableHead className="bg-background group-hover/row:bg-muted">
                      {t('installations:configuration.table.cnCode')}
                    </TableHead>
                    <TableHead className="bg-background group-hover/row:bg-muted">
                      {t(
                        'installations:suppliers.edit.installations.edit.emissionReports.fields.productionRoute'
                      )}
                    </TableHead>
                    <TableHead className="bg-background group-hover/row:bg-muted">
                      {t(
                        'installations:suppliers.edit.installations.edit.emissionReports.fields.specificEmbeddedEmissions'
                      )}
                    </TableHead>
                    <TableHead className="bg-background group-hover/row:bg-muted">
                      {t(
                        'installations:suppliers.edit.installations.edit.emissionReports.fields.proofFile'
                      )}
                    </TableHead>
                    {canEdit && (
                      <TableHead className="w-[120px] bg-background text-end group-hover/row:bg-muted">
                        {t('installations:configuration.table.actions')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} className="group/row">
                      <TableCell className="bg-background group-hover/row:bg-muted">
                        {row.year}
                      </TableCell>
                      <TableCell className="bg-background group-hover/row:bg-muted">
                        {row.cnCode}
                      </TableCell>
                      <TableCell className="bg-background group-hover/row:bg-muted">
                        {formatSupplierInstallationEmissionReportProductionRouteLabel(
                          row.productionRoute,
                          t
                        )}
                      </TableCell>
                      <TableCell className="bg-background tabular-nums group-hover/row:bg-muted">
                        {row.specificEmbeddedEmissions}{' '}
                        {t('common:units.tCO2ePerT')}
                      </TableCell>
                      <TableCell className="bg-background group-hover/row:bg-muted">
                        {row.proofFilePath ? (
                          <Button
                            type="button"
                            variant="link"
                            className="h-auto px-0"
                            onClick={() => handleDownload(row.proofFilePath!)}
                          >
                            {row.proofFilePath}
                          </Button>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {t(
                              'installations:suppliers.edit.installations.edit.emissionReports.fields.noFile'
                            )}
                          </span>
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell
                          className={cn(
                            'bg-background group-hover/row:bg-muted',
                            'text-end'
                          )}
                        >
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              aria-label={t(
                                'installations:suppliers.edit.installations.edit.emissionReports.a11y.editReport',
                                { cnCode: row.cnCode, year: row.year }
                              )}
                              onClick={() => setEditReport(row)}
                              disabled={isMutating}
                            >
                              <Pencil className="size-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="text-destructive"
                              aria-label={t(
                                'installations:suppliers.edit.installations.edit.emissionReports.a11y.deleteReport',
                                { cnCode: row.cnCode, year: row.year }
                              )}
                              onClick={() => {
                                setPendingDelete({
                                  id: row.id,
                                  label: `${row.cnCode} (${row.year})`,
                                })
                                setDeleteDialogOpen(true)
                              }}
                              disabled={isMutating}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
    </>
  )

  return (
    <>
      {variant === 'plain' ? (
        <div className="space-y-4">
          {createButton ? <div className="flex justify-end">{createButton}</div> : null}
          {listContent}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <CardTitle>
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.title'
                  )}
                </CardTitle>
                <p className="mt-1 text-sm font-normal text-muted-foreground">
                  {t(
                    'installations:suppliers.edit.installations.edit.emissionReports.description'
                  )}
                </p>
              </div>
              {createButton}
            </div>
          </CardHeader>
          <CardContent>{listContent}</CardContent>
        </Card>
      )}

      <SupplierInstallationEmissionReportFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        isSubmitting={mutations.createEmissionReport.isPending}
        onCreate={handleCreate}
        onEdit={handleEdit}
      />

      <SupplierInstallationEmissionReportFormDialog
        open={editReport !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditReport(null)
          }
        }}
        mode="edit"
        report={editReport}
        isSubmitting={mutations.updateEmissionReport.isPending}
        onCreate={handleCreate}
        onEdit={handleEdit}
      />

      <SupplierInstallationEmissionReportDeleteConfirmDialog
        pendingDelete={pendingDelete}
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null)
            setDeleteDialogOpen(false)
          } else {
            setDeleteDialogOpen(true)
          }
        }}
        onConfirm={handleConfirmDelete}
        isLoading={mutations.deleteEmissionReport.isPending}
      />
    </>
  )
}
